import React, { useEffect, useState, Children, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, FlaskConical, Microscope, ShieldCheck, ArrowRight, CheckCircle2, AlertCircle, Lock, ShoppingCart, ClipboardList, Star, Truck, Shield, Zap, Brain, Dumbbell, Moon, Heart, Leaf, DollarSign, BadgeCheck, Play, Sprout, ChevronLeft, ChevronRight } from 'lucide-react';
import { shopData, Category, Product } from './shopData';
import { useCart } from '../../../contexts/CartContext';
import { usePrice } from '../../../contexts/PriceContext';
const FORMSPREE_ENDPOINT = 'https://formspree.io/f/xkoyvnke';
const fadeUpVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.2, 0.65, 0.3, 0.9]
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08
    }
  }
};
// Stylized bottle mockup — CSS-only since real product photos don't exist yet
function BottleMockup({
  product
}: {
  product: Product;
}) {
  const colorMap = {
    charcoal: 'bg-charcoal',
    burgundy: 'bg-burgundy',
    navyslate: 'bg-navyslate',
    tealslate: 'bg-tealslate'
  };
  const accentMap = {
    tealslate: 'bg-tealslate',
    lightteal: 'bg-lightteal'
  };
  return <div className="relative w-full h-full flex items-end justify-center pb-4" data-id="element-1927">
      {/* Bottle */}
      <div className="relative w-[55%] aspect-[1/2.2] flex flex-col" data-id="element-1928">
        {/* Cap */}
        <div className={`${accentMap[product.accentColor]} h-[12%] rounded-t-md mx-2 shadow-md`} data-id="element-1929" />
        {/* Neck */}
        <div className={`${colorMap[product.bottleColor]} h-[5%] mx-3 opacity-70`} data-id="element-1930" />
        {/* Body */}
        <div className={`${colorMap[product.bottleColor]} flex-1 rounded-md shadow-2xl relative overflow-hidden border border-white/5`} data-id="element-1931">
          {/* Highlight */}
          <div className="absolute top-2 left-2 bottom-2 w-[8%] bg-white/10 rounded-full blur-sm" data-id="element-1932" />

          {/* Label */}
          <div className="absolute inset-x-3 top-[20%] bottom-[20%] bg-[#F5F5F0] flex flex-col items-center justify-between p-3 shadow-inner" data-id="element-1933">
            {/* Top: brand */}
            <div className="text-center" data-id="element-1934">
              <p className="text-[6px] md:text-[7px] font-bold text-[#1F1F1F] uppercase tracking-[0.2em]" data-id="element-1935">
                Effective Dose
              </p>
              <div className={`w-3 h-[1px] ${accentMap[product.accentColor]} mx-auto mt-1`} data-id="element-1936" />
            </div>

            {/* Middle: product name */}
            <div className="text-center" data-id="element-1937">
              <p className="text-[8px] md:text-[10px] font-serif text-[#1F1F1F] leading-tight" data-id="element-1938">
                {product.name}
              </p>
            </div>

            {/* Bottom: dose */}
            <div className="text-center" data-id="element-1939">
              <p className="text-[14px] md:text-[18px] font-serif font-bold text-[#1F1F1F] leading-none" data-id="element-1940">
                {product.dose}
              </p>
              <p className="text-[5px] md:text-[6px] text-[#7A7A70] uppercase tracking-widest mt-0.5" data-id="element-1941">
                {product.doseUnit}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Floor shadow */}
      <div className="absolute bottom-0 left-1/4 right-1/4 h-2 bg-black/30 rounded-full blur-md" data-id="element-1942" />
    </div>;
}
interface WaitlistFormProps {
  productName?: string;
}
function WaitlistForm({
  productName
}: WaitlistFormProps) {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [error, setError] = useState('');
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    if (formData.get('_gotcha')) {
      setStatus('success');
      return;
    }
    setStatus('submitting');
    setError('');
    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'application/json'
        }
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data?.error || 'Something went wrong.');
        setStatus('error');
      }
    } catch {
      setError('Network error. Try again.');
      setStatus('error');
    }
  };
  if (status === 'success') {
    return <div className="bg-tealslate/20 border-l-4 border-tealslate p-6 flex gap-4 items-start" data-id="element-1943">
        <CheckCircle2 className="w-6 h-6 text-tealslate shrink-0" data-id="element-1944" />
        <div data-id="element-1945">
          <p className="font-bold text-white mb-1" data-id="element-1946">You're on the list.</p>
          <p className="text-sm text-[#D8D8D0]" data-id="element-1947">
            We'll email you the moment Effective Dose launches.
          </p>
        </div>
      </div>;
  }
  return <form onSubmit={handleSubmit} className="w-full" data-id="element-1948">
      <input type="text" name="_gotcha" tabIndex={-1} autoComplete="off" style={{
      position: 'absolute',
      left: '-9999px'
    }} aria-hidden="true" data-id="element-1949" />
      <input type="hidden" name="form_type" value="effective_dose_waitlist" data-id="element-1950" />
      {productName && <input type="hidden" name="product_interest" value={productName} data-id="element-1951" />}

      <div className="flex flex-col sm:flex-row gap-3" data-id="element-1952">
        <input type="email" name="email" placeholder="Enter your email" required className="flex-1 bg-bodybg border border-darkgray px-5 py-4 text-white placeholder:text-[#7A7A70] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1953" />
        <button type="submit" disabled={status === 'submitting'} className="px-8 py-4 bg-tealslate text-white text-sm font-bold uppercase tracking-wider hover:bg-lightteal transition-colors disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2 whitespace-nowrap" data-id="element-1954">
          {status === 'submitting' ? <>
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1955" />
              Joining…
            </> : <>
              Notify Me <ArrowRight className="w-4 h-4" data-id="element-1956" />
            </>}
        </button>
      </div>

      {status === 'error' && <div className="mt-3 flex items-center gap-2 text-sm text-burgundy" data-id="element-1957">
          <AlertCircle className="w-4 h-4" data-id="element-1958" />
          {error}
        </div>}
    </form>;
}
export function Shop() {
  const [activeCategory, setActiveCategory] = useState<Category | 'All'>('All');
  const {
    addItem
  } = useCart();
  const categories: (Category | 'All')[] = ['All', 'Performance', 'Foundation', 'Recovery'];
  const filteredProducts = activeCategory === 'All' ? shopData : shopData.filter(p => p.category === activeCategory);
  return <div className="w-full min-h-screen bg-bodybg" data-id="element-1959">
      {/* HERO CAROUSEL */}
      <HeroCarousel data-id="element-1960" />

      {/* TRUST STRIP */}
      <section className="bg-[#F5F5F0] border-b border-[#E5E5E0] py-10 px-6" data-id="element-1961">
        <div className="max-w-6xl mx-auto" data-id="element-1962">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 items-center" data-id="element-1963">
            {[{
            icon: ClipboardList,
            title: 'Formulated by',
            subtitle: 'Registered Dietitians'
          }, {
            icon: Star,
            title: 'Trusted by 1,000+',
            subtitle: 'Happy Customers'
          }, {
            icon: Truck,
            title: 'Free Shipping on Orders',
            subtitle: 'Over $75'
          }].map((item, i) => <div key={i} className="flex items-center justify-center md:justify-start gap-4" data-id="element-1964">
                <div className="w-12 h-12 rounded-full border border-tealslate/40 flex items-center justify-center shrink-0" data-id="element-1965">
                  <item.icon className="w-5 h-5 text-tealslate" data-id="element-1966" />
                </div>
                <div className="flex flex-col leading-tight" data-id="element-1967">
                  <span className="text-sm font-semibold text-[#1F1F1F]" data-id="element-1968">
                    {item.title}
                  </span>
                  <span className="text-sm text-[#5A5A5A]" data-id="element-1969">
                    {item.subtitle}
                  </span>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* SHOP BY WELLNESS GOAL */}
      <section className="bg-white py-16 md:py-20 px-6 border-b border-[#E5E5E0]" data-id="element-1970">
        <div className="max-w-7xl mx-auto" data-id="element-1971">
          <div className="text-center mb-12" data-id="element-1972">
            <p className="text-[10px] font-bold text-tealslate uppercase tracking-[0.25em] mb-3" data-id="element-1973">
              Shop by Wellness Goal
            </p>
            <h2 className="text-3xl md:text-4xl font-serif text-[#1F1F1F]" data-id="element-1974">
              Find what your body needs.
            </h2>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5" data-id="element-1975">
            {[{
            icon: Shield,
            title: 'Immunity',
            desc: 'Support your body\u2019s natural defenses'
          }, {
            icon: Zap,
            title: 'Energy',
            desc: 'Boost energy and reduce fatigue'
          }, {
            icon: Brain,
            title: 'Mood & Stress',
            desc: 'Promote calm, focus, and balance'
          }, {
            icon: Dumbbell,
            title: 'Muscle & Performance',
            desc: 'Build and preserve lean mass'
          }, {
            icon: Moon,
            title: 'Sleep',
            desc: 'Deeper, more restorative rest'
          }, {
            icon: Heart,
            title: 'Heart & Recovery',
            desc: 'Support cardiovascular health'
          }].map((item, i) => <a key={i} href="#products" className="group bg-[#F5F5F0] border border-[#E5E5E0] rounded-xl px-5 py-7 flex flex-col items-center text-center hover:shadow-md hover:border-tealslate/40 transition-all" data-id="element-1976">
                <item.icon className="w-8 h-8 text-tealslate mb-4" data-id="element-1977" />
                <h3 className="text-xs font-bold text-[#1F1F1F] uppercase tracking-[0.15em] mb-2" data-id="element-1978">
                  {item.title}
                </h3>
                <p className="text-xs text-[#5A5A5A] leading-relaxed mb-4 flex-1" data-id="element-1979">
                  {item.desc}
                </p>
                <span className="inline-flex items-center gap-1 text-[10px] font-bold text-tealslate uppercase tracking-[0.2em] group-hover:gap-2 transition-all" data-id="element-1980">
                  Shop Now <ArrowRight className="w-3 h-3" data-id="element-1981" />
                </span>
              </a>)}
          </div>
        </div>
      </section>

      {/* BEST SELLERS */}
      <BestSellersSection addItem={addItem} data-id="element-1982" />

      {/* BETTER TOGETHER / BUNDLES */}
      <section className="relative bg-cover bg-center py-10 md:py-14 px-6" style={{
      backgroundImage: `url('https://cdn.magicpatterns.com/uploads/7dpxWz9tbndZ9rBRG4gWur/file_00000000a17871fd95f08a8df36d7999.png')`
    }} data-id="element-1983">
        <div className="max-w-7xl mx-auto" data-id="element-1984">
          <div className="grid grid-cols-1 lg:grid-cols-12" data-id="element-1985">
            <div className="lg:col-span-6" data-id="element-1986">
              <h3 className="text-3xl md:text-4xl font-serif text-[#1F1F1F] mb-3" data-id="element-1987">
                Better Together
              </h3>
              <p className="text-sm md:text-base text-[#5A5A5A] mb-5 max-w-sm" data-id="element-1988">
                Save more with our expert-curated wellness bundles.
              </p>
              <a href="#products" className="inline-block px-7 py-3 bg-tealslate text-white text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#4A5A5F] transition-colors rounded mb-8" data-id="element-1989">
                Shop Bundles
              </a>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 max-w-2xl" data-id="element-1990">
                {[{
                icon: Leaf,
                title: 'Curated by Dietitians',
                desc: 'Expert combinations that work synergistically'
              }, {
                icon: DollarSign,
                title: 'Save More',
                desc: 'Bundle and save on your daily essentials'
              }, {
                icon: BadgeCheck,
                title: 'Simplify Your Routine',
                desc: 'Everything you need, in one convenient set'
              }].map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-1991">
                    <div className="w-9 h-9 rounded-full border border-tealslate/40 bg-white/70 backdrop-blur-sm flex items-center justify-center flex-shrink-0" data-id="element-1992">
                      <item.icon className="w-4 h-4 text-tealslate" data-id="element-1993" />
                    </div>
                    <div data-id="element-1994">
                      <p className="text-xs font-semibold text-[#1F1F1F] mb-0.5" data-id="element-1995">
                        {item.title}
                      </p>
                      <p className="text-[11px] text-[#5A5A5A] leading-relaxed" data-id="element-1996">
                        {item.desc}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRODUCT GRID */}
      <section id="products" className="bg-white py-20 px-6 scroll-mt-24 scroll-smooth" data-id="element-1997">
        <div className="max-w-7xl mx-auto" data-id="element-1998">
          <h2 className="text-sm md:text-base font-bold text-[#1F1F1F] uppercase tracking-[0.25em] mb-8" data-id="element-1999">
            All Products
          </h2>
          <motion.div key={activeCategory} initial="hidden" animate="visible" variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-2000">
            {filteredProducts.map(product => <motion.div key={product.id} variants={fadeUpVariants} className="bg-white border border-[#E5E5E0] rounded-xl flex flex-col h-full hover:border-tealslate/60 hover:shadow-md transition-all group overflow-hidden" data-id="element-2001">
                {/* Bottle area */}
                <div className="relative aspect-square bg-[#F5F5F0] overflow-hidden" data-id="element-2002">
                  <span className="absolute top-4 left-4 text-[10px] font-bold text-tealslate uppercase tracking-widest z-10 px-2 py-1 bg-white/90 backdrop-blur-sm rounded" data-id="element-2003">
                    {product.category}
                  </span>
                  <img src={product.image} alt={product.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-2004" />
                </div>

                {/* Content */}
                <div className="p-6 flex flex-col flex-1" data-id="element-2005">
                  <h3 className="text-2xl font-serif text-[#1F1F1F] mb-2 leading-tight" data-id="element-2006">
                    {product.name}
                  </h3>
                  <p className="text-sm text-tealslate font-serif italic mb-4" data-id="element-2007">
                    {product.tagline}
                  </p>

                  <p className="text-sm text-[#3B3B3B] leading-relaxed mb-4" data-id="element-2008">
                    {product.shortDescription}
                  </p>

                  <details className="text-sm group/details" data-id="element-2009">
                    <summary className="cursor-pointer list-none flex items-center justify-between gap-2 py-3 border-t border-[#E5E5E0] text-tealslate hover:text-[#2A3A3F] transition-colors" data-id="element-2010">
                      <span className="text-xs font-bold uppercase tracking-wider" data-id="element-2011">
                        Why this dose?
                      </span>
                      <span className="group-open/details:rotate-45 transition-transform" data-id="element-2012">
                        +
                      </span>
                    </summary>
                    <p className="text-sm text-[#5A5A5A] leading-relaxed pt-3" data-id="element-2013">
                      {product.whyEffective}
                    </p>
                  </details>

                  <ProductPricing product={product} addItem={addItem} data-id="element-2014" />
                </div>
              </motion.div>)}
          </motion.div>
        </div>
      </section>
    </div>;
}
const BEST_SELLER_IDS = ['multivitamin-mens', 'omega-3', 'creatine', 'vitamin-d-k2', 'protein'];
const BEST_SELLER_META: Record<string, {
  rating: number;
  reviews: number;
  tag?: string;
}> = {
  'multivitamin-mens': {
    rating: 4.8,
    reviews: 627,
    tag: 'BEST SELLER'
  },
  'omega-3': {
    rating: 4.7,
    reviews: 513
  },
  creatine: {
    rating: 4.9,
    reviews: 812
  },
  'vitamin-d-k2': {
    rating: 4.6,
    reviews: 412
  },
  protein: {
    rating: 4.8,
    reviews: 389
  }
};
const HERO_SLIDES = [{
  bg: 'https://cdn.magicpatterns.com/uploads/jZM2tEohB8P3RXVY53wDZo/file_0000000050e471fd9b72564486af2c12.png',
  eyebrow: 'Science-backed. Dietitian approved.',
  headlineStart: 'Supplements that support your',
  headlineEnd: 'best life.',
  subhead: 'High-quality, evidence-based supplements to help you feel better, every day.',
  primaryCta: {
    label: 'Shop All Supplements',
    href: '#products'
  },
  secondaryCta: {
    label: 'Our Promise',
    href: '#products'
  }
}, {
  bg: 'https://cdn.magicpatterns.com/uploads/f87EatSxUMfcpQZBHWscm9/file_00000000dce071fd9bbf2806563c1bcd.png',
  eyebrow: 'No fairy dust. No fillers.',
  headlineStart: 'Effective doses.',
  headlineEnd: 'Always.',
  subhead: 'Every product is dosed at the level research demands, not the level that protects margins.',
  primaryCta: {
    label: 'Browse Products',
    href: '#products'
  },
  secondaryCta: {
    label: 'Why It Matters',
    href: '#products'
  }
}, {
  bg: 'https://cdn.magicpatterns.com/uploads/jZM2tEohB8P3RXVY53wDZo/file_0000000050e471fd9b72564486af2c12.png',
  eyebrow: 'No proprietary blends. No junk.',
  headlineStart: 'Clinical evidence,',
  headlineEnd: 'in your routine.',
  subhead: 'Formulated by a Registered Dietitian who actually reads the research. Designed for your real-world results.',
  primaryCta: {
    label: 'Browse Products',
    href: '#products'
  },
  secondaryCta: {
    label: 'Shop the Line',
    href: '#products'
  }
}];
function HeroCarousel() {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    const t = setInterval(() => {
      setIndex(i => (i + 1) % HERO_SLIDES.length);
    }, 6500);
    return () => clearInterval(t);
  }, []);
  const slide = HERO_SLIDES[index];
  const heroBottles = [shopData.find(p => p.id === 'multivitamin-mens'), shopData.find(p => p.id === 'omega-3'), shopData.find(p => p.id === 'creatine')].filter((p): p is Product => Boolean(p));
  const next = () => setIndex(i => (i + 1) % HERO_SLIDES.length);
  const prev = () => setIndex(i => (i - 1 + HERO_SLIDES.length) % HERO_SLIDES.length);
  return <section className="relative pt-28 pb-12 md:pb-16 px-6 overflow-hidden" data-id="element-2015">
      {/* Animated background image */}
      <AnimatePresence mode="wait" data-id="element-2016">
        <motion.div key={`bg-${index}`} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.8
      }} className="absolute inset-0 bg-cover bg-center" style={{
        backgroundImage: `url('${slide.bg}')`
      }} data-id="element-2017" />
      </AnimatePresence>

      {/* Dark gradient overlay for text readability */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/65 to-black/40 pointer-events-none" data-id="element-2018" />

      <div className="relative max-w-7xl mx-auto" data-id="element-2019">
        <div className="min-h-[520px] flex items-center" data-id="element-2020">
          {/* Carousel Text */}
          <div className="relative w-full" data-id="element-2021">
            <AnimatePresence mode="wait" data-id="element-2022">
              <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -20
            }} transition={{
              duration: 0.5
            }} data-id="element-2023">
                <p className="text-[10px] md:text-xs font-bold text-cream/90 uppercase tracking-[0.25em] mb-6" data-id="element-2024">
                  {slide.eyebrow}
                </p>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-white leading-[1.1] mb-2" data-id="element-2025">
                  {slide.headlineStart}
                </h1>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-cream italic leading-[1.1] mb-6" data-id="element-2026">
                  {slide.headlineEnd}
                </h1>
                <p className="text-base md:text-lg text-white/80 mb-8 max-w-md leading-relaxed" data-id="element-2027">
                  {slide.subhead}
                </p>
                <div className="flex flex-col sm:flex-row items-start gap-4 mb-10" data-id="element-2028">
                  <a href={slide.primaryCta.href} className="px-7 py-3.5 bg-tealslate text-white text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#4A5A5F] transition-colors" data-id="element-2029">
                    {slide.primaryCta.label}
                  </a>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Trust badges row (static, shared across slides) */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-lg" data-id="element-2030">
              {[{
              icon: Leaf,
              label: 'Dietitian Formulated'
            }, {
              icon: FlaskConical,
              label: 'Premium Ingredients'
            }, {
              icon: CheckCircle2,
              label: 'Third-Party Tested'
            }, {
              icon: Sprout,
              label: 'No Artificial Fillers'
            }].map((b, i) => <div key={i} className="flex flex-col items-start text-left" data-id="element-2031">
                  <b.icon className="w-5 h-5 text-cream mb-2" data-id="element-2032" />
                  <span className="text-[11px] text-white/85 leading-tight" data-id="element-2033">
                    {b.label}
                  </span>
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}
interface BestSellersSectionProps {
  addItem: ReturnType<typeof useCart>['addItem'];
}
function BestSellersSection({
  addItem
}: BestSellersSectionProps) {
  const products = BEST_SELLER_IDS.map(id => shopData.find(p => p.id === id)).filter((p): p is Product => Boolean(p));
  return <section className="bg-white py-14 md:py-16 px-6" data-id="element-2034">
      <div className="max-w-7xl mx-auto" data-id="element-2035">
        <div className="flex items-end justify-between mb-8" data-id="element-2036">
          <h2 className="text-sm md:text-base font-bold text-[#1F1F1F] uppercase tracking-[0.25em]" data-id="element-2037">
            Best Sellers
          </h2>
          <a href="#products" className="text-xs text-[#5A5A5A] hover:text-tealslate transition-colors inline-flex items-center gap-1" data-id="element-2038">
            View all best sellers <ArrowRight className="w-3 h-3" data-id="element-2039" />
          </a>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5" data-id="element-2040">
          {products.map(product => <BestSellerCard key={product.id} product={product} addItem={addItem} meta={BEST_SELLER_META[product.id]} data-id="element-2041" />)}
        </div>
      </div>
    </section>;
}
interface BestSellerCardProps {
  product: Product;
  addItem: ReturnType<typeof useCart>['addItem'];
  meta?: {
    rating: number;
    reviews: number;
    tag?: string;
  };
}
function BestSellerCard({
  product,
  addItem,
  meta
}: BestSellerCardProps) {
  const {
    cents
  } = usePrice(product.lookupKey, product.price);
  const canBuy = cents !== null && Boolean(product.lookupKey);
  const rating = meta?.rating ?? 4.8;
  const reviews = meta?.reviews ?? 200;
  const handleAdd = () => {
    if (cents === null || !product.lookupKey) return;
    addItem({
      productId: product.id,
      name: product.name,
      unitPrice: cents,
      type: 'supplement',
      lookupKey: product.lookupKey
    });
  };
  return <div className="flex flex-col relative bg-white border border-[#E5E5E0] rounded-xl p-4 hover:shadow-md transition-shadow" data-id="element-2042">
      <div className="aspect-square bg-white overflow-hidden mb-3 flex items-center justify-center relative" data-id="element-2043">
        {meta?.tag && <span className="absolute top-2 left-2 z-10 px-2 py-1 bg-charcoal text-white text-[9px] font-bold uppercase tracking-widest rounded" data-id="element-2044">
            {meta.tag}
          </span>}
        <img src={product.image} alt={product.name} className="w-full h-full object-contain p-3 scale-[1.17]" data-id="element-2045" />
      </div>
      <h3 className="text-base font-normal text-[#1F1F1F] mb-[5px] leading-tight" data-id="element-2046">
        {product.name}
      </h3>
      <div className="flex items-center gap-1.5 mb-2" data-id="element-2047">
        <div className="flex items-center" data-id="element-2048">
          {[1, 2, 3, 4, 5].map(i => <Star key={i} className={`w-3 h-3 ${i <= Math.floor(rating) ? 'fill-tealslate text-tealslate' : 'text-[#D8D8D0]'}`} data-id="element-2049" />)}
        </div>
        <span className="text-[10px] text-[#5A5A5A]" data-id="element-2050">({reviews})</span>
      </div>
      <p className="text-base font-serif text-[#1F1F1F] mb-3" data-id="element-2051">
        {cents !== null ? `${(cents / 100).toFixed(2)}` : '—'}
      </p>
      <button onClick={handleAdd} disabled={!canBuy} className="mt-auto w-full px-3 py-2.5 bg-tealslate text-white text-[10px] font-bold uppercase tracking-[0.15em] hover:bg-lightteal transition-colors disabled:opacity-40 disabled:cursor-not-allowed rounded" data-id="element-2052">
        Add to Cart
      </button>
    </div>;
}
interface ProductPricingProps {
  product: Product;
  addItem: ReturnType<typeof useCart>['addItem'];
}
function ProductPricing({
  product,
  addItem
}: ProductPricingProps) {
  const {
    cents,
    isLive,
    loading
  } = usePrice(product.lookupKey, product.price);
  const canBuy = cents !== null && Boolean(product.lookupKey);
  const handleAdd = () => {
    if (cents === null || !product.lookupKey) return;
    addItem({
      productId: product.id,
      name: product.name,
      unitPrice: cents,
      type: 'supplement',
      lookupKey: product.lookupKey
    });
  };
  return <div className="flex items-center justify-between gap-4 pt-5 mt-auto border-t border-[#E5E5E0]" data-id="element-2053">
      <div data-id="element-2054">
        <p className="text-[10px] font-bold text-[#5A5A5A] uppercase tracking-widest mb-0.5" data-id="element-2055">
          Price
        </p>
        {cents === null ? <div className="h-7 w-20 bg-[#E5E5E0] animate-pulse rounded-sm" data-id="element-2056" /> : <p className="text-2xl font-serif text-[#1F1F1F] leading-none" data-id="element-2057">
            ${(cents / 100).toFixed(2)}
            {!isLive && !loading && <span className="text-[10px] text-[#7A7A70] font-sans ml-2 normal-case tracking-normal" data-id="element-2058">
                · est.
              </span>}
          </p>}
      </div>
      <button onClick={handleAdd} disabled={!canBuy} className="px-5 py-3 bg-tealslate text-white text-xs font-bold uppercase tracking-wider hover:bg-lightteal transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2" title={!canBuy ? 'Not yet available for purchase' : undefined} data-id="element-2059">
        <ShoppingCart className="w-3.5 h-3.5" data-id="element-2060" />
        Add to Cart
      </button>
    </div>;
}